import { Close, EditNote } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Checkbox from "../../../../../../../Components/CheckBox/Checkbox";
import {
  primaryButtonStyles,
  tertiaryButtonStyles,
  textButtonStyles,
} from "../../../../../../../Constants/buttonStyles";
import { dialogStyles } from "../../../../../../../Constants/dialogStyles";
import { inputStyles } from "../../../../../../../Constants/inputStyles";
import theme, {
  createLLMTypography,
} from "../../../../../../../Constants/theme";
import { validateNeo4jCreds } from "../../../../../../../features/chatbots/chatbotSlice";
import { CONTEXTUAL_RETRIEVAL_PROMPT } from "../../../../../../../utils/defaultPrompts";
import { nodeInputStyles } from "../../GeneralNodeStyles";
import { textfieldLayout } from "../RagHyperparametersStyles";

const RetrievalMethod = memo(
  ({
    data,
    retrievalMethod,
    setRetrievalMethod,
    organization,
    graphRagCredentials,
    setGraphRagCredentials,
    neo4jPrompts,
    setNeo4jPrompts,
    setOpenKeys,
  }) => {
    const { botData, setBotData, nodeId } = data;
    const [open, setOpen] = useState(false);
    const [open_CR_PromptDialog, setOpen_CR_PromptDialog] = useState(false);
    const [contextRetrievalPrompt, setContextRetrievalPrompt] = useState(
      botData?.retrieval_prompts?.contextual_retrieval_prompt ||
        CONTEXTUAL_RETRIEVAL_PROMPT
    );
    const [originalPrompt, setOriginalPrompt] = useState(
      CONTEXTUAL_RETRIEVAL_PROMPT
    );
    const [isSaveEnabled, setIsSaveEnabled] = useState(false);

    const [validateNeo4jCredsLoading, setvalidateNeo4jCredsLoading] =
      useState(false);

    const [enableGraphOptions, setEnableGraphOptions] = useState(false);

    const [openChunkWindowDialog, setOpenChunkWindowDialog] = useState(false);
    const [chunkWindowSize, setChunkWindowSize] = useState(
      botData?.retrieval_prompts?.chunk_window_size || "2"
    );

    const [localEntityRelationshipPrompt, setLocalEntityRelationshipPrompt] =
      useState(neo4jPrompts?.KG_TRIPLET_EXTRACT_TMPL || "");

    // to store if neo 4j credentials opened by clicking on option
    const [selectedGraphRagOption, setSelectedGraphRagOption] = useState(null);

    const dispatch = useDispatch();

    // Check if OpenAI key exists and is validated
    const openaiKeyExists =
      organization?.organization?.llm_keys?.openai_key?.key;

    useEffect(() => {
      const _RetrievalPrompts_ = JSON.parse(
        localStorage.getItem(`retrieval_prompts_${nodeId}`)
      );
      if (_RetrievalPrompts_) {
        setContextRetrievalPrompt(
          _RetrievalPrompts_.contextual_retrieval_prompt
        );
        setOriginalPrompt(_RetrievalPrompts_.contextual_retrieval_prompt);

        setChunkWindowSize(_RetrievalPrompts_.chunk_window_size);
      }
    }, []);

    useEffect(() => {
      // Check if all fields are filled
      const { NEO4J_URI, NEO4J_USERNAME, NEO4J_PASSWORD, llm } =
        graphRagCredentials;
      const allFieldsFilled =
        NEO4J_URI && NEO4J_USERNAME && NEO4J_PASSWORD && llm;

      setIsSaveEnabled(allFieldsFilled);
    }, [graphRagCredentials]);

    useEffect(() => {
      const creds = JSON.parse(localStorage.getItem(`neo4j_credentials`));
      if (creds) {
        setGraphRagCredentials(creds);
        setEnableGraphOptions(true);
      }
    }, []);

    const handleOpenDialog = (graphRagOption = null) => {
      setSelectedGraphRagOption(graphRagOption);
      setOpen(true);
    };

    const handleCloseDialog = () => {
      setOpen(false);
    };

    const handleOpen_CR_Dialog = () => {
      setOpen_CR_PromptDialog(true);
    };

    const handleClose_CR_Dialog = () => {
      setOpen_CR_PromptDialog(false);
    };

    const handleRetrievalMethodChange = (checked, type) => {
      const method = { ...retrievalMethod };

      if (type.startsWith("graphrag") && !openaiKeyExists) {
        setOpenKeys(true);
        return;
      }

      method[type] = checked;

      if (!Object.values(method).some((value) => value)) {
        method["basic"] = true;
      }

      method[type] = checked;

      if (!Object.values(method).some((value) => value)) {
        method["basic"] = true;
      }
      setRetrievalMethod({ ...method });
      localStorage.setItem(`retrievalMethod_${nodeId}`, JSON.stringify(method));
      setBotData((prevData) => ({
        ...prevData,
        workflow: {
          ...prevData.workflow,
          [`${nodeId}`]: {
            ...prevData.workflow[`${nodeId}`],
            retrieval_method: method,
          },
        },
      }));
    };

    const handleSaveCredentials = async () => {
      const { NEO4J_URI, NEO4J_USERNAME, NEO4J_PASSWORD, llm } =
        graphRagCredentials;

      // Regular expression to validate Neo4j URI
      const neo4jUriPattern =
        /^(neo4j|bolt)(\+s|\+ssc)?:\/\/[^\/\s:@]+(:\d+)?$/;

      if (!NEO4J_URI || !NEO4J_USERNAME || !NEO4J_PASSWORD || !llm) {
        // alert("Please fill out all fields.");
        data.showSnackbar("Please fill out all fields.", "warning");
        return;
      }

      // Validate the NEO4J_URI
      if (!neo4jUriPattern.test(NEO4J_URI)) {
        // alert("Please enter a valid Neo4j URI.");
        data.showSnackbar("Please enter a valid Neo4j URI.", "error");
        return;
      }

      // Validation for Entity Relationship Prompt
      if (!localEntityRelationshipPrompt.trim()) {
        data.showSnackbar(
          "Entity Relationship Prompt cannot be empty.",
          "error"
        );
        return;
      }

      // Ensure the prompt contains all required lines
      let updatedPrompt = localEntityRelationshipPrompt.trim();

      // Validation for Entity Relationship Prompt
      const requiredLines = [
        `{max_knowledge_triplets}`,
        `Format each entity as ("entity"$$$$<entity_name>$$$$<entity_type>$$$$<entity_description>)`,
        `Format each relationship as ("relationship"$$$$<source_entity>$$$$<target_entity>$$$$<relation>$$$$<relationship_description>)`,
        `text: {text}`,
        `output:`,
      ];

      requiredLines.forEach((line) => {
        if (!updatedPrompt.includes(line)) {
          updatedPrompt += `\n${line}`;
        }
      });

      // Ensure the prompt is always within triple quotes
      if (!updatedPrompt.startsWith(`""`)) {
        updatedPrompt = `""\n${updatedPrompt}`;
      }
      if (!updatedPrompt.endsWith(`""`)) {
        updatedPrompt += `\n""`;
      }

      // Make API call to validate credentials
      try {
        setvalidateNeo4jCredsLoading(true);
        data.showSnackbar(
          "Validating Neo4j credentials, Please wait.",
          "warning"
        );
        const req = { neo4j_credentials: graphRagCredentials };

        const res = await dispatch(validateNeo4jCreds(req));

        if (res.payload === "Neo4j credentials are valid.") {
          // if (res) {
          // Credentials are valid
          // Save credentials to localStorage and botData
          localStorage.setItem(
            "neo4j_credentials",
            JSON.stringify(graphRagCredentials)
          );
          setBotData((prevData) => ({
            ...prevData,
            neo4j_credentials: graphRagCredentials,
          }));

          setBotData((prevData) => ({
            ...prevData,
            workflow: {
              ...prevData.workflow,
              [`${nodeId}`]: {
                ...prevData.workflow[`${nodeId}`],
              },
            },
          }));

          setLocalEntityRelationshipPrompt(updatedPrompt);

          setNeo4jPrompts((prev) => ({
            ...prev,
            KG_TRIPLET_EXTRACT_TMPL: updatedPrompt,
          }));

          setBotData((prevData) => ({
            ...prevData,
            neo_4j_prompts: {
              KG_TRIPLET_EXTRACT_TMPL: updatedPrompt,
            },
          }));

          localStorage.setItem(
            `neo_4j_prompts_${nodeId}`,
            JSON.stringify({
              ...neo4jPrompts,
              KG_TRIPLET_EXTRACT_TMPL: updatedPrompt,
            })
          );

          setEnableGraphOptions(true);

          if (!openaiKeyExists) {
            setOpenKeys(true);
            // Close the dialog
            setOpen(false);
            setSelectedGraphRagOption(null);
            return;
          }

          // automatically enable the selected GraphRag option
          if (selectedGraphRagOption) {
            const updatedRetrievalMethod = {
              ...retrievalMethod,
              [selectedGraphRagOption]: true,
            };
            setRetrievalMethod(updatedRetrievalMethod);

            localStorage.setItem(
              `retrievalMethod_${nodeId}`,
              JSON.stringify(updatedRetrievalMethod)
            );

            setBotData((prevData) => ({
              ...prevData,
              workflow: {
                ...prevData.workflow,
                [`${nodeId}`]: {
                  ...prevData.workflow[`${nodeId}`],
                  retrieval_method: updatedRetrievalMethod,
                },
              },
            }));
          }
          // Close the dialog
          setOpen(false);
          setSelectedGraphRagOption(null);
        } else {
          // Backend returned an error
          data.showSnackbar(
            `${res.payload || "Failed to validate credentials."}`,
            "error"
          );
        }
      } catch (error) {
        console.error("Error validating credentials:", error);
        data.showSnackbar(
          "An error occurred while validating credentials.",
          "error"
        );
      } finally {
        setvalidateNeo4jCredsLoading(false);
      }
    };

    const handleChange = (event) => {
      const { name, value } = event.target;
      setGraphRagCredentials((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave_CR_Dialog = () => {
      // Ensure the prompt contains the required tags with correct format
      const documentRegex =
        /<document>[\s\S]*?{complete_document}[\s\S]*?<\/document>/;
      const chunkRegex = /<chunk>[\s\S]*?{text}[\s\S]*?<\/chunk>/;

      let updatedPrompt = contextRetrievalPrompt;

      if (!documentRegex.test(updatedPrompt)) {
        updatedPrompt =
          `<document>\n    {complete_document}\n    </document>\n` +
          updatedPrompt;
      } else {
        // Ensure {complete_document} is present within the document tags
        updatedPrompt = updatedPrompt.replace(
          /<document>[\s\S]*?<\/document>/,
          (match) => {
            if (!match.includes("{complete_document}")) {
              return `<document>\n    {complete_document}\n    </document>`;
            }
            return match;
          }
        );
      }

      if (!chunkRegex.test(updatedPrompt)) {
        updatedPrompt += `\n<chunk>\n    {text}\n    </chunk>`;
      } else {
        // Ensure {text} is present within the chunk tags
        updatedPrompt = updatedPrompt.replace(
          /<chunk>[\s\S]*?<\/chunk>/,
          (match) => {
            if (!match.includes("{text}")) {
              return `<chunk>\n    {text}\n    </chunk>`;
            }
            return match;
          }
        );
      }

      if (!updatedPrompt.startsWith(`"""`)) {
        updatedPrompt = `"""` + updatedPrompt;
      }
      if (!updatedPrompt.endsWith(`"""`)) {
        updatedPrompt += `"""`;
      }

      localStorage.setItem(
        `retrieval_prompts_${nodeId}`,
        JSON.stringify({
          contextual_retrieval_prompt: updatedPrompt,
          chunk_window_size: chunkWindowSize,
        })
      );
      setBotData((prevData) => ({
        ...prevData,
        retrieval_prompts: {
          ...prevData.retrieval_prompts,
          contextual_retrieval_prompt: updatedPrompt,
        },
      }));
      setContextRetrievalPrompt(updatedPrompt);
      setOriginalPrompt(updatedPrompt);
      handleClose_CR_Dialog();
    };

    const handleReset_CR_Dialog = () => {
      setContextRetrievalPrompt(CONTEXTUAL_RETRIEVAL_PROMPT);
    };

    const handleOpenChunkWindowDialog = () => {
      setOpenChunkWindowDialog(true);
    };

    const handleCloseChunkWindowDialog = () => {
      setOpenChunkWindowDialog(false);
    };

    const handleSaveChunkWindowSize = () => {
      setBotData((prevData) => ({
        ...prevData,
        retrieval_prompts: {
          ...prevData.retrieval_prompts,
          chunk_window_size: chunkWindowSize,
        },
      }));
      localStorage.setItem(
        `retrieval_prompts_${nodeId}`,
        JSON.stringify({
          contextual_retrieval_prompt: contextRetrievalPrompt,
          chunk_window_size: chunkWindowSize,
        })
      );
      handleCloseChunkWindowDialog();
    };

    const handleEntityRelationshipPromptChange = (event) => {
      const updatedValue = event.target.value;
      setLocalEntityRelationshipPrompt(updatedValue);
    };

    return (
      <Box sx={nodeInputStyles}>
        <Box
          sx={{
            fontSize: createLLMTypography.fontSizes.sectionTitle,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Checkbox
            label="Basic"
            onChange={(event) =>
              handleRetrievalMethodChange(event.target.checked, "basic")
            }
            checked={retrievalMethod?.basic}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <Checkbox
              label="Chunk Window"
              onChange={(event) =>
                handleRetrievalMethodChange(
                  event.target.checked,
                  "sentence-window"
                )
              }
              checked={retrievalMethod?.["sentence-window"]}
              tooltipText={
                organization?.organization?.subscription === "starter" &&
                "You need Pro subscription to access this feature."
              }
              tooltipPlacement="left"
            />
            <EditNote
              onClick={
                organization?.organization?.subscription === "starter"
                  ? null
                  : handleOpenChunkWindowDialog
              }
              fontSize="small"
              sx={{
                ml: 3,
                cursor:
                  organization?.organization?.subscription === "starter"
                    ? "default"
                    : "pointer",
                opacity:
                  organization?.organization?.subscription === "starter"
                    ? 0.5
                    : 1,
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
            }}
          >
            <Checkbox
              label={"Contextual Retrieval"}
              disabled={organization?.organization?.subscription === "starter"}
              tooltipText={
                organization?.organization?.subscription === "starter" &&
                "You need Pro subscription to access this feature."
              }
              tooltipPlacement="left"
              onChange={(event) =>
                handleRetrievalMethodChange(
                  event.target.checked,
                  "context_retrieval"
                )
              }
              checked={retrievalMethod?.context_retrieval}
            />

            <EditNote
              onClick={
                organization?.organization?.subscription === "starter"
                  ? null
                  : handleOpen_CR_Dialog
              }
              fontSize="small"
              sx={{
                ml: 3,
                cursor:
                  organization?.organization?.subscription === "starter"
                    ? "default"
                    : "pointer",
                opacity:
                  organization?.organization?.subscription === "starter"
                    ? 0.5
                    : 1,
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              borderRadius: "4px",
              border: `1px solid ${theme.border.light}`,
              flexDirection: "column",
              px: 1,
              pb: 1,
            }}
          >
            <Tooltip title="GraphRag" placement="left">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "100%",
                  fontSize: createLLMTypography.fontSizes.sectionTitle,
                }}
              >
                <Typography
                  sx={{
                    fontSize: createLLMTypography.fontSizes.sectionTitle,
                  }}
                >
                  GraphRAG Options
                </Typography>
                <EditNote
                  onClick={
                    organization?.organization?.subscription !== "starter"
                      ? handleOpenDialog
                      : () => null
                  }
                  fontSize="small"
                  sx={{
                    cursor:
                      organization?.organization?.subscription === "starter"
                        ? "default"
                        : "pointer",
                    opacity:
                      organization?.organization?.subscription === "starter"
                        ? 0.5
                        : 1,
                  }}
                />
              </Box>
            </Tooltip>

            <Box
              sx={{
                display: "flex",
                borderRadius: "4px",
                border: `1px solid ${theme.border.light}`,
                flexDirection: "column",
                mt: 1,
                fontSize: createLLMTypography.fontSizes.sectionText1,
                gap: 0.5,
              }}
            >
              <Checkbox
                // disabled={true}
                label={<span>With Standard Retrieval</span>}
                onChange={
                  // () => null
                  enableGraphOptions
                    ? (event) =>
                        handleRetrievalMethodChange(
                          event.target.checked,
                          "graphrag+basic"
                        )
                    : () => handleOpenDialog("graphrag+basic")
                }
                checked={retrievalMethod?.["graphrag+basic"]}
                // tooltipText={"This feature will be available soon."}
                // tooltipPlacement="left"
              />
              <Checkbox
                // disabled={true}
                label={<span>With Chunk Window</span>}
                onChange={
                  // () => null
                  enableGraphOptions
                    ? (event) =>
                        handleRetrievalMethodChange(
                          event.target.checked,
                          "graphrag+sentence-window"
                        )
                    : () => handleOpenDialog("graphrag+sentence-window")
                }
                checked={retrievalMethod?.["graphrag+sentence-window"]}
                // tooltipText={"This feature will be available soon."}
                // tooltipPlacement="left"
              />
              <Checkbox
                // disabled={true}
                label={<span>With Context Retrieval</span>}
                onChange={
                  // () => null
                  enableGraphOptions
                    ? (event) =>
                        handleRetrievalMethodChange(
                          event.target.checked,
                          "graphrag+context_retrieval"
                        )
                    : () => handleOpenDialog("graphrag+context_retrieval")
                }
                checked={retrievalMethod?.["graphrag+context_retrieval"]}
                // tooltipText={"This feature will be available soon."}
                // tooltipPlacement="left"
              />
            </Box>
          </Box>
        </Box>
        <Dialog
          open={open}
          onClose={handleCloseDialog}
          fullWidth={true}
          maxWidth={"md"}
          PaperProps={{ sx: dialogStyles.dialogRoot }}
        >
          <DialogTitle sx={dialogStyles.dialogTitle}>
            <Typography sx={dialogStyles.dialogTitleText}>
              Neo4J Credentials
            </Typography>
            <IconButton
              sx={dialogStyles.dialogCloseIcon}
              onClick={handleCloseDialog}
              size="small"
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ ...dialogStyles.dialogContent, mt: 2 }}>
            <div>
              <Typography
                variant="caption"
                sx={{ color: theme.textColors.dark.primary, mb: 1 }}
              >
                NEO4J URI
              </Typography>
              <TextField
                name="NEO4J_URI"
                value={graphRagCredentials.NEO4J_URI}
                onChange={handleChange}
                fullWidth
                margin="dense"
                required
                sx={inputStyles.textField}
              />
            </div>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                width: "100%",
                alignItems: "center",
              }}
            >
              <div style={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.textColors.dark.primary, mb: 1 }}
                >
                  NEO4J Username
                </Typography>
                <TextField
                  name="NEO4J_USERNAME"
                  value={graphRagCredentials.NEO4J_USERNAME}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  required
                  sx={inputStyles.textField}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.textColors.dark.primary, mb: 1 }}
                >
                  NEO4J Password
                </Typography>
                <TextField
                  name="NEO4J_PASSWORD"
                  value={graphRagCredentials.NEO4J_PASSWORD}
                  onChange={handleChange}
                  type="password"
                  fullWidth
                  margin="dense"
                  required
                  sx={inputStyles.textField}
                />
              </div>
              <FormControl
                fullWidth
                margin="dense"
                required
                sx={{ mt: 0, width: "20%" }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: theme.textColors.dark.primary, pb: 1 }}
                >
                  LLM
                </Typography>
                <Select
                  name="llm"
                  value={graphRagCredentials?.llm || ""}
                  onChange={handleChange}
                  sx={{ ...inputStyles.select, pb: 1 }}
                >
                  <MenuItem value="gpt-4o-mini">gpt-4o-mini</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <div>
              <Typography
                variant="caption"
                sx={{ color: theme.textColors.dark.primary, mb: 1 }}
              >
                Entity-Relationship Prompt
              </Typography>
              <TextField
                name="Entity_Relationship_Prompt"
                value={localEntityRelationshipPrompt}
                onChange={handleEntityRelationshipPromptChange}
                fullWidth
                multiline
                rows={10}
                margin="dense"
                sx={inputStyles.textField}
              />
            </div>
          </DialogContent>
          <DialogActions sx={dialogStyles.dialogActions}>
            <Button sx={textButtonStyles} onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              sx={primaryButtonStyles}
              onClick={handleSaveCredentials}
              disabled={!isSaveEnabled || validateNeo4jCredsLoading}
            >
              {validateNeo4jCredsLoading ? (
                <CircularProgress size={"14px"} />
              ) : (
                "Save"
              )}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Context Retrieval Prompt Dialog */}
        <Dialog
          open={open_CR_PromptDialog}
          onClose={handleClose_CR_Dialog}
          fullWidth={true}
          maxWidth={"md"}
          PaperProps={{ sx: dialogStyles.dialogRoot }}
        >
          <DialogTitle sx={dialogStyles.dialogTitle}>
            <Typography sx={dialogStyles.dialogTitleText}>
              Edit Context Retrieval Prompt
            </Typography>
            <IconButton
              sx={dialogStyles.dialogCloseIcon}
              onClick={handleClose_CR_Dialog}
              size="small"
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ ...dialogStyles.dialogContent, mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={contextRetrievalPrompt}
              onChange={(e) => setContextRetrievalPrompt(e.target.value)}
              placeholder="Edit the prompt here"
              sx={inputStyles.textField}
            />
          </DialogContent>
          <DialogActions sx={dialogStyles.dialogActions}>
            <Button onClick={handleReset_CR_Dialog} sx={tertiaryButtonStyles}>
              Reset
            </Button>
            <Button
              onClick={handleSave_CR_Dialog}
              sx={primaryButtonStyles}
              disabled={
                contextRetrievalPrompt === originalPrompt ||
                contextRetrievalPrompt?.trim() === ""
              }
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
        {/* Chunk Window Dialog */}
        <Dialog
          open={openChunkWindowDialog}
          onClose={handleCloseChunkWindowDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: dialogStyles.dialogRoot }}
        >
          <DialogTitle sx={dialogStyles.dialogTitle}>
            <Typography sx={dialogStyles.dialogTitleText}>
              Edit Chunk Window Size
            </Typography>
            <IconButton
              sx={dialogStyles.dialogCloseIcon}
              onClick={handleCloseChunkWindowDialog}
              size="small"
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ ...dialogStyles.dialogContent, mt: 2 }}>
            <FormControl fullWidth>
              <DialogContentText sx={dialogStyles.dialogContentText}>
                Chunk Window Size
              </DialogContentText>
              <Tooltip title="Number of chunks to include before and after">
                <Select
                  value={chunkWindowSize}
                  onChange={(e) => setChunkWindowSize(e.target.value)}
                  sx={inputStyles.select}
                  // MenuProps={{
                  //   PaperProps: {
                  //     sx: { ...menuContainerStyles },
                  //   },
                  // }}
                >
                  <MenuItem value="1">1</MenuItem>
                  <MenuItem value="2">2</MenuItem>
                  <MenuItem value="3">3</MenuItem>
                </Select>
              </Tooltip>
            </FormControl>
          </DialogContent>
          <DialogActions sx={dialogStyles.dialogActions}>
            <Button
              onClick={handleCloseChunkWindowDialog}
              sx={tertiaryButtonStyles}
            >
              Cancel
            </Button>
            <Button
              sx={primaryButtonStyles}
              onClick={handleSaveChunkWindowSize}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);

export default RetrievalMethod;
